import { motion } from 'framer-motion';

interface IconProps {
  className?: string;
  animate?: boolean;
}

export const AIIcon = ({ className = "w-12 h-12", animate = false }: IconProps) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    animate={animate ? { scale: [1, 1.1, 1] } : {}}
    transition={{ duration: 2, repeat: Infinity }}
  >
    <defs>
      <linearGradient id="aiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0ff6fc" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
    </defs>
    <path
      d="M12 2L13.09 8.26L20.5 7L15.5 12L20.5 17L13.09 15.74L12 22L10.91 15.74L3.5 17L8.5 12L3.5 7L10.91 8.26L12 2Z"
      fill="url(#aiGradient)"
    />
    <circle cx="12" cy="12" r="3" fill="#000" />
    <motion.circle
      cx="12"
      cy="12"
      r="1.5"
      fill="#0ff6fc"
      animate={animate ? { opacity: [1, 0.5, 1] } : {}}
      transition={{ duration: 1.5, repeat: Infinity }}
    />
  </motion.svg>
);

export const WebDevIcon = ({ className = "w-12 h-12", animate = false }: IconProps) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    animate={animate ? { rotateY: [0, 360] } : {}}
    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
  >
    <defs>
      <linearGradient id="webGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7c3aed" />
        <stop offset="100%" stopColor="#00ff99" />
      </linearGradient>
    </defs>
    <rect x="2" y="4" width="20" height="14" rx="2" stroke="url(#webGradient)" strokeWidth="2" fill="none" />
    <path d="M2 8h20" stroke="url(#webGradient)" strokeWidth="2" />
    <circle cx="5" cy="6" r="0.5" fill="#0ff6fc" />
    <circle cx="7" cy="6" r="0.5" fill="#00ff99" />
    <circle cx="9" cy="6" r="0.5" fill="#7c3aed" />
    <motion.path
      d="M7 12l2 2 4-4"
      stroke="#0ff6fc"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      animate={animate ? { pathLength: [0, 1, 0] } : {}}
      transition={{ duration: 2, repeat: Infinity }}
    />
  </motion.svg>
);

export const AutomationIcon = ({ className = "w-12 h-12", animate = false }: IconProps) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="autoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00ff99" />
        <stop offset="100%" stopColor="#0ff6fc" />
      </linearGradient>
    </defs>
    <motion.circle
      cx="12"
      cy="12"
      r="8"
      stroke="url(#autoGradient)"
      strokeWidth="2"
      fill="none"
      animate={animate ? { rotate: 360 } : {}}
      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
    />
    <motion.circle
      cx="12"
      cy="12"
      r="4"
      stroke="url(#autoGradient)"
      strokeWidth="1.5"
      fill="none"
      animate={animate ? { rotate: -360 } : {}}
      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
    />
    <circle cx="12" cy="12" r="2" fill="url(#autoGradient)" />
    <motion.path
      d="M12 4v2M12 18v2M4 12h2M18 12h2"
      stroke="#0ff6fc"
      strokeWidth="2"
      strokeLinecap="round"
      animate={animate ? { opacity: [1, 0.3, 1] } : {}}
      transition={{ duration: 1, repeat: Infinity }}
    />
  </motion.svg>
);

export const CloudIcon = ({ className = "w-12 h-12", animate = false }: IconProps) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="cloudGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7c3aed" />
        <stop offset="100%" stopColor="#0ff6fc" />
      </linearGradient>
    </defs>
    <motion.path
      d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"
      fill="url(#cloudGradient)"
      animate={animate ? { y: [-2, 2, -2] } : {}}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.circle
      cx="10"
      cy="14"
      r="1"
      fill="#000"
      animate={animate ? { scale: [1, 1.2, 1] } : {}}
      transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
    />
    <motion.circle
      cx="14"
      cy="14"
      r="1"
      fill="#000"
      animate={animate ? { scale: [1, 1.2, 1] } : {}}
      transition={{ duration: 1.5, repeat: Infinity, delay: 1 }}
    />
  </motion.svg>
);

export const SecurityIcon = ({ className = "w-12 h-12", animate = false }: IconProps) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="securityGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00ff99" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
    </defs>
    <motion.path
      d="M12 2L4 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-8-4z"
      fill="url(#securityGradient)"
      animate={animate ? { scale: [1, 1.05, 1] } : {}}
      transition={{ duration: 2, repeat: Infinity }}
    />
    <motion.path
      d="M9 12l2 2 4-4"
      stroke="#000"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      animate={animate ? { pathLength: [0, 1] } : {}}
      transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
    />
  </motion.svg>
);

export const TransformIcon = ({ className = "w-16 h-16", animate = false }: IconProps) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="transformGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0ff6fc" />
        <stop offset="50%" stopColor="#7c3aed" />
        <stop offset="100%" stopColor="#00ff99" />
      </linearGradient>
    </defs>
    <motion.path
      d="M12 2L2 7l10 5 10-5-10-5z"
      fill="url(#transformGradient)"
      animate={animate ? { scale: [1, 1.1, 1] } : {}}
      transition={{ duration: 2, repeat: Infinity, delay: 0 }}
    />
    <motion.path
      d="M2 17l10 5 10-5"
      stroke="url(#transformGradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      animate={animate ? { pathLength: [0, 1, 0] } : {}}
      transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
    />
    <motion.path
      d="M2 12l10 5 10-5"
      stroke="url(#transformGradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      animate={animate ? { pathLength: [0, 1, 0] } : {}}
      transition={{ duration: 3, repeat: Infinity, delay: 1 }}
    />
  </motion.svg>
);
